import React, {
  useState,
  useCallback,
  useEffect,
  useContext,
  useMemo,
  useRef
} from 'react';
import hive from '@bombas/hive';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';

import { ApplicationContext } from 'shop/context/ApplicationContext';
import isGiftCardSendEnabled from 'shop/lib/util/giftCardSendConfig';
import { useApolloClient } from '@apollo/client';
import * as Styled from './ProductDetails.styled';
import { useProductState } from '../../Product.context';
import { splitArray } from '../../../../util/util';
import * as MapUtils from '../../../../util/MapUtils';
import Swatch from '../../../Swatch/Swatch';
import { decide } from '../../../../util/optimizely';
import { trackEvent } from '../../../../util/track';
import SelectTileGroup, {
  SelectTile
} from '../SelectTileGroup/SelectTileGroup.styled';
import ButtonRow from '../../../ButtonRow/ButtonRow';
import ProductDonationCount from '../ProductDonationCount/ProductDonationCount';
import ProductPrice from '../ProductPrice/ProductPrice';
import NotifyDialog from '../../../NotifyDialog/NotifyDialog';
import SizeGuideModalWithData from '../../../SizeGuideModal/SizeGuideModal';
import { ContainerQueryContext } from '../../../../util/ContainerQuery.context';
import { useQuickView } from '../../../QuickView/QuickView.context';
import { ProductDetailsFragment } from './ProductDetails.gql';
import ProductPropTypes, {
  colorPropType,
  productAttributePropType,
  sizesPropType,
  variantPropType
} from '../../Product.propTypes';
import RichTextPropTypes from '../../../../prop-types/rich-text';
import Icon from '../../../Icons/Icon';
import RichTextModal from '../../../RichTextModal/RichTextModal';
import MessageTier from '../../../MessageTier/MessageTier';
import { fetchSavings } from './ProductDetails.utils';
import { isBrowser } from '../../../../util/ssr';
import { QBS_ENABLED } from '../../../../util/const';
import ImageCarouselSmall from '../ResponsiveImageGallery/components/ImageCarouselSmall/ImageCarouselSmall';
import RelatedProductSelector from '../../../RelatedProductSelector/RelatedProductSelector';
import ProductRatingV2 from '../ProductRating/ProductRatingV2';
import GiftCardForm from '../GiftCard/GiftCardForm';
import LinkText from '../../../LinkText/LinkText';
import useGiftCardSend from '../../../../hooks/useGiftCardSend';

/**
 * Custom hook to send an event when viewing a changed product variant.
 * @param {Object} - product data
 * @returns Object - event
 */
export const useTrackProductVariantView = ({
  id: productId,
  colors,
  variants,
  title,
  gender,
  item_count,
  product_type,
  currentVariant,
  currentItem
}) => {
  useEffect(() => {
    // Generate an object of swatches, of `variant_id:swatch`
    const swatchMap =
      colors &&
      Array.isArray(colors) &&
      Object.fromEntries(
        colors.map((productColor) => {
          const { id } =
            (variants &&
              Array.isArray(variants) &&
              variants.find(
                (productVariant) => productVariant.color === productColor.id
              )) ||
            {};

          return [id, productColor.fill];
        })
      );

    // event added for GA4
    const clearData = {
      ecommerce: null
    };

    const event = {
      event: 'viewProductVariant',
      ecommerce: {
        items: [currentItem]
      },
      product: {
        data: {
          id: productId,
          title,
          gender,
          item_count,
          product_type,
          swatches: swatchMap,
          variant: currentVariant && {
            id: currentVariant.externalSystems?.shopify,
            images: currentVariant.images?.map((image) => image.src),
            available: currentVariant.available,
            price:
              currentVariant.price?.discountPrice ||
              currentVariant.price?.basePrice,
            currency: currentVariant.price?.currency,
            pack_savings: `${
              currentVariant.price && currentVariant.price.discount
            }%`
          }
        }
      }
    };

    trackEvent(clearData);
    trackEvent(event);
  }, [currentVariant]);
};

/**
 * A `ProductDetails` component displays all of the relevant information about a given product.
 */
const ProductDetails = ({
  callouts,
  categories,
  className,
  colors,
  config,
  guides,
  id: productId,
  images,
  onAddToBag,
  onShowFullDetails,
  relatedProducts,
  shopifyId,
  sizes,
  title,
  url,
  variants
}) => {
  const router = useRouter();
  const client = useApolloClient();
  const { currentVariant, allVariants, selectedOptions, setOption } =
    useProductState();
  const { qbsDiscountData } = useContext(ApplicationContext);
  const [isNotifyOpen, setIsNotifyOpen] = useState(false);
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  const [isShippingAndReturnOpen, setShippingAndReturnOpen] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [isAddToCartSuccess, setIsAddToCartSuccess] = useState(false);
  const [isQBSButtonLoading, setIsQBSButtonLoading] = useState(false);
  const [qbsAddToCartStatus, setQbsAddToCartStatus] = useState({
    index: 0,
    status: 0
  });
  const [savings, setSavings] = useState(null);
  const [isOfferDetailsOpen, setOfferDetailsOpen] = useState(false);
  const [isQBSQTYButtonEnable, setIsQBSQTYButtonEnable] = useState(false);

  const { isGiftCard, handleGiftCardAdded } = useGiftCardSend(
    {
      categories,
      externalSystems: { shopify: shopifyId }
    },
    currentVariant
  );

  const [isGiftCardSendFeatureEnabled, setIsGiftCardSendFeatureEnabled] =
    useState(isGiftCard);
  /**
   * urlParams and showQBS to be removed QBS when user testing and A/B testing is finished
   */
  const urlParams = new URLSearchParams(
    isBrowser() ? window.location.search : ''
  );
  const forceQtyButton = urlParams && urlParams.get('enable_qty_btn');

  const { styleQueryMode } = useContext(ContainerQueryContext);
  const { isQuickView, setActiveProductId, setHandle, previousUrl } =
    useQuickView();

  // compile data on current variant for sending to GTM data layer for GA4
  const currentItem = useMemo(
    () => ({
      item_id: productId,
      item_name: currentVariant.title,
      affiliation: 'Bombas',
      currency: currentVariant?.price?.currency,
      item_brand: 'Bombas',
      item_category: currentVariant.category,
      item_category2: currentVariant.gender,
      item_variant: currentVariant?.title,
      price: parseFloat(currentVariant?.price?.price, 10),
      quantity: 1,
      sku: currentVariant.sku
    }),
    [currentVariant.id]
  );

  useEffect(() => {
    async function setQbsData() {
      if (qbsDiscountData) {
        const {
          components,
          sku,
          itemCount,
          categories: variantCategories
        } = currentVariant;

        const qbsSavings = fetchSavings(
          variantCategories,
          components[0],
          sku,
          itemCount,
          qbsDiscountData
        );

        if (qbsSavings?.length > 0) {
          let isQBSButtonEnable = false;
          if (!forceQtyButton) {
            // Only run experiment when user is not forcing experiment,
            // otherwise it will skew test results.
            const featureDecisionQBSButtons = await decide('qbs_qty_buttons');
            isQBSButtonEnable =
              featureDecisionQBSButtons?.variationKey === 'test';
          } else {
            isQBSButtonEnable = forceQtyButton === 'true';
          }

          /**
           * params to be removed once user tests and A/B tests are completed
           * the params bypass the requirement of being bucketed into 'test' variant to set savings
           */
          if (QBS_ENABLED) {
            setIsQBSQTYButtonEnable(isQBSButtonEnable);
            setSavings(qbsSavings);
          }
        } else {
          /**
           * Reset savings state when changing products in QuickView via RelatedProductSelector,
           * since the SPA doesn't automatically update state on product change.
           */
          setSavings(null);
        }
      }
    }
    setQbsData();
  }, [qbsDiscountData, currentVariant]);

  useEffect(() => {
    if (isSizeGuideOpen) {
      hive.track('SIZE_GUIDE_LINK_CLICK');
    }
  }, [isSizeGuideOpen]);

  const handleAddToBag = useCallback(async () => {
    setIsButtonLoading(true);

    try {
      await onAddToBag(currentVariant.externalSystems?.shopify);

      setIsButtonLoading(false);
      setIsAddToCartSuccess(true);

      setTimeout(() => {
        setIsAddToCartSuccess(false);
      }, 2000);
    } catch (_err) {
      setIsButtonLoading(false);
    }
  }, [currentVariant.id]);

  const handleQBSAddToBag = useCallback(
    async (index, quantity) => {
      setIsQBSButtonLoading(true);

      try {
        await onAddToBag(currentVariant.externalSystems?.shopify, quantity);

        setIsQBSButtonLoading(false);
        setQbsAddToCartStatus({
          index,
          status: 1
        });
        hive.track('QBS_TIER_SELECT', { tierIndex: index + 1 });

        setTimeout(() => {
          setQbsAddToCartStatus({
            index,
            status: 0
          });
        }, 2000);
      } catch (_err) {
        setQbsAddToCartStatus({
          index,
          status: 0
        });
        setIsQBSButtonLoading(false);
      }
    },
    [currentVariant.id]
  );

  const handleSelectOption = useCallback((event) => {
    if (event.currentTarget.name === 'color') {
      hive.track(`${isQuickView ? 'QUICKVIEW' : 'PDP'}_COLOR_SWATCH_CLICK`);
    }

    if (event.currentTarget.name === 'size') {
      hive.track(`${isQuickView ? 'QUICKVIEW' : 'PDP'}_SIZE_SELECTOR_CLICK`);
    }

    setOption(event.currentTarget.name, event.currentTarget.value);
  }, []);

  const handleShowNotify = useCallback(() => {
    setIsNotifyOpen(true);
  });

  const renderButtonText = () => {
    if (isAddToCartSuccess) return 'Added To Bag';
    if (currentVariant.available) return 'Add To Bag';

    return 'Option Not Available';
  };

  // Track an event with relevant data every time the product variant changes.
  useTrackProductVariantView({
    id: productId,
    colors,
    variants,
    title: currentVariant.title,
    gender: currentVariant.gender,
    item_count: currentVariant.itemCount,
    product_type: currentVariant.category,
    currentVariant,
    currentItem
  });

  useEffect(() => {
    const checkGiftCardSendFeature = async () => {
      try {
        const query = router?.query || {};
        const enabled = await isGiftCardSendEnabled(client, query);

        setIsGiftCardSendFeatureEnabled(enabled);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error checking Gift Card Send feature flag:', error);
        setIsGiftCardSendFeatureEnabled(false);
      }
    };
    if (client) {
      checkGiftCardSendFeature();
    }
  }, [client, router?.query]);

  const giftCardFormRef = useRef(null);
  const [isGiftLoading, setIsGiftLoading] = useState(false);
  const handleGiftCardSubmit = async (values, actions) => {
    const giftData = {
      name: values.recipientName,
      email: values.recipientEmail,
      message: values.giftMessage,
      send_at: values.sendDate
    };

    try {
      await handleGiftCardAdded({
        gift: giftData,
        isQuickView,
        setActiveProductId,
        previousUrl
      });
      actions.setSubmitting(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error submitting gift card form (Submit):', error);
      actions.setSubmitting(false);
      throw error;
    } finally {
      setIsGiftLoading(false);
    }
  };
  const handleAddToBagWithGiftData = async () => {
    setIsGiftLoading(true);
    if (!giftCardFormRef.current) {
      return;
    }
    try {
      await giftCardFormRef.current.submitForm();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error submitting gift card form:', error);
    } finally {
      setIsGiftLoading(false);
    }
  };

  /**
   * Renders price, QBS discount, related products, and auto applied message
   */
  const relatedProductsAndPriceElement = (
    <Styled.OrderedContainer $styleQueryMode={styleQueryMode}>
      {currentVariant.price && (
        <Styled.PriceQBSDiscountContainer
          $percentOff={currentVariant.price.percentOff}
        >
          <ProductPrice
            data-testid="detailsProductPrice"
            percentOff={currentVariant.price.percentOff}
            originalPrice={currentVariant.price.basePrice}
            discountPrice={currentVariant.price.discountPrice}
            currencyCode={currentVariant.price.currency}
            discountCopy={config?.discount_copy?.value}
            hasMultipleItems={currentVariant.itemCount > 1}
          />
          {savings?.length > 0 && (
            <Styled.QBSContainer>
              <MessageTier
                savings={savings}
                setQBSModalOpen={setOfferDetailsOpen}
              />
            </Styled.QBSContainer>
          )}
        </Styled.PriceQBSDiscountContainer>
      )}
      {relatedProducts && !!relatedProducts.length && (
        <Styled.RelatedProductsContainer>
          <RelatedProductSelector
            onChange={(path) => {
              hive.track('PACK_SLIDER_CLICK');

              if (isQuickView) {
                setHandle(path);
              } else {
                const params = new URLSearchParams();

                if (selectedOptions.has('color')) {
                  params.set('variant', selectedOptions.get('color'));
                }

                const sizeKeys = sizes.map((s) => s.id);

                selectedOptions.forEach((value, key) => {
                  if (sizeKeys.includes(key)) {
                    params.set(key, value);
                  }
                });

                const queryString = params.toString();
                const urlWithParams = queryString
                  ? `${path}?${queryString}`
                  : path;
                router.push(urlWithParams);
              }
            }}
            relatedProducts={relatedProducts}
            showSubcopy={false}
            currentProduct={url}
          />
        </Styled.RelatedProductsContainer>
      )}
      {isBrowser() && !isGiftCard && <Styled.AutoApplyMessage />}
    </Styled.OrderedContainer>
  );

  const upperWrapperElements = (
    <>
      <Styled.Header
        $styleQueryMode={styleQueryMode}
        data-label="productDetails_title"
      >
        {title}
      </Styled.Header>
    </>
  );

  const hasColors = colors && colors.length > 0;

  const renderQBSButton = (savingObject, index) => {
    const isAdded =
      qbsAddToCartStatus.index === index && qbsAddToCartStatus.status === 1;
    const buttonText = isAdded ? 'Added!' : `Add ${savingObject.quantity}`;
    const subCopyText = isAdded ? '' : `Save ${savingObject.discount}%`;
    const shouldUseTwoLines = !isAdded && savings.length > 2;
    return (
      <Styled.QBSButton
        key={`QBS_Tier_${index}`}
        data-label={`QBS_Tier_${index + 1}`}
        isLoading={isQBSButtonLoading}
        isDisabled={!currentVariant.available}
        onClick={() => handleQBSAddToBag(index, savingObject.quantity)}
        variant="secondary"
        subCopy={{
          text: subCopyText,
          twoLines: shouldUseTwoLines
        }}
      >
        {buttonText}
      </Styled.QBSButton>
    );
  };

  const swatchesElement = (
    <>
      <Styled.ProductOptions
        data-label="productDetails_swatchGroup"
        title="Color"
        name="color"
        onChange={handleSelectOption}
        selectedOption={selectedOptions.get('color')}
        minimumOptionsRequired={1}
      >
        {colors.map(({ id, title: colorTitle, swatch: { file } }) => (
          <Swatch
            key={id}
            value={id}
            label={colorTitle}
            fill={`https:${file.url}`}
            size={39}
            labelDataLabel="productDetails_swatchColor"
          />
        ))}
      </Styled.ProductOptions>
    </>
  );

  const middleWrapperElements = (
    <>
      {hasColors && (
        <Styled.SwatchesElement $styleQueryMode={styleQueryMode}>
          {swatchesElement}
        </Styled.SwatchesElement>
      )}
      {relatedProductsAndPriceElement}
      {sizes &&
        sizes.length > 0 &&
        sizes.map((sizeGroup, index) => {
          const allOptions = [...selectedOptions.keys()];
          const [start, end] = splitArray(allOptions, sizeGroup.id).map(
            (group) => group.map((o) => selectedOptions.get(o))
          );
          const isFirstSizeGroup = index === 0;
          const ConditionalContainer = isFirstSizeGroup
            ? Styled.RelativeContainer
            : React.Fragment;

          return (
            <ConditionalContainer
              key={sizeGroup.title}
              data-label="productDetails_sizeGroup"
            >
              {!!guides.length && isFirstSizeGroup && (
                <>
                  <Styled.SizeGuideLink
                    size="Regular"
                    underline
                    data-label="size_guide_link"
                    href="/pages/size-guide"
                    onClick={(e) =>
                      e.preventDefault() || setIsSizeGuideOpen(true)
                    }
                  >
                    Size Guide
                  </Styled.SizeGuideLink>
                  <SizeGuideModalWithData
                    isOpen={isSizeGuideOpen}
                    onRequestClose={() => setIsSizeGuideOpen(false)}
                    query={guides}
                  />
                </>
              )}
              <Styled.ProductOptions
                title={sizeGroup.title}
                name={sizeGroup.id}
                onChange={handleSelectOption}
                selectedOption={selectedOptions.get(sizeGroup.id)}
                Container={<SelectTileGroup tiles={sizeGroup.options.length} />}
              >
                {sizeGroup?.options.map(({ id, value, guidance }) => (
                  <SelectTile
                    data-label="productDetails_sizeSelect"
                    key={id}
                    value={id}
                    label={value}
                    note={
                      (!isGiftCard &&
                        currentVariant.category !== 'UNISEX' &&
                        guidance) ||
                      ''
                    }
                    isUnavailable={
                      // NOTE: This allows us to dynamically set the availability status of each option tile.
                      // Because our variant map is a tree of options, we keep the currently selected option(s) as constants
                      // and inject the option being rendered as variable.
                      !MapUtils.deepGet(allVariants, [...start, id, ...end], {})
                        .available
                    }
                  />
                ))}
              </Styled.ProductOptions>
            </ConditionalContainer>
          );
        })}
      {!currentVariant.available &&
      currentVariant.replenishable &&
      currentVariant.price ? (
        <Styled.AddToBagContainer
          data-label="add_to_bag"
          aria-labelledby="notify me button"
        >
          <Styled.AddToBagButton
            data-label="add_to_bag_button"
            data-testid="AddToBagButton"
            variant="defaultDark"
            isFull
            size="x-large"
            data-cnstrc-btn="add_to_wishlist"
            onClick={handleShowNotify}
          >
            Notify Me
          </Styled.AddToBagButton>
        </Styled.AddToBagContainer>
      ) : (
        <>
          <Styled.AddToBagContainer
            data-label="add_to_bag"
            aria-labelledby={renderButtonText()}
          >
            {isGiftCard && isGiftCardSendFeatureEnabled && (
              <GiftCardForm
                ref={giftCardFormRef}
                onSubmit={handleGiftCardSubmit}
              />
            )}
            {/** This add to bag button is shown when the product is a gift card and giftCardSendFeatureEnabled is true */}
            {isGiftCard && isGiftCardSendFeatureEnabled && (
              <Styled.AddToBagButton
                data-label="add_to_bag_button"
                data-testid="AddToBagButton"
                variant="primary"
                isFull
                isLoading={isGiftLoading}
                isDisabled={!currentVariant.available || isGiftLoading}
                size="x-large"
                onClick={handleAddToBagWithGiftData}
                data-cnstrc-btn="add_to_cart"
                style={{ marginTop: '24px' }}
              >
                {renderButtonText()}
              </Styled.AddToBagButton>
            )}
            {/** This add to bag button is only shown when the product is NOT a gift card or giftCardSendFeatureEnabled is false */}
            {!(isGiftCard && isGiftCardSendFeatureEnabled) && (
              <Styled.AddToBagButton
                data-label="add_to_bag_button"
                data-testid="AddToBagButton"
                variant="primary"
                isFull
                isLoading={isButtonLoading}
                isDisabled={!currentVariant.available}
                size="x-large"
                onClick={handleAddToBag}
                data-cnstrc-btn="add_to_cart"
              >
                {renderButtonText()}
              </Styled.AddToBagButton>
            )}
          </Styled.AddToBagContainer>

          {isQBSQTYButtonEnable && savings?.length > 0 && (
            <Styled.QBSButtonContainer>
              {savings.map(renderQBSButton)}
            </Styled.QBSButtonContainer>
          )}
        </>
      )}
      {!!config?.donation_categories?.value
        .split(',')
        .map((donation_category) => donation_category.toLowerCase().trim())
        .filter((donation_category) =>
          categories.some(
            (category) => category?.toLowerCase() === donation_category
          )
        ).length && <ProductDonationCount count={currentVariant.itemCount} />}
    </>
  );

  const lowerWrapperElements = (
    <Styled.DetailLinks>
      {/** this is temporarily needed for ensuring the buttonRow shows on the current implementation of single tier QBS when there are more than one active product-discount in Shopify */}
      {/* {savings?.length === 1 && (
        <ButtonRow
          ariaLabel="QBS Mix and Match"
          title={`Buy ${savings[0].quantity}+ ${savings[0].displayName}, Save ${savings[0].discount}%`}
          subtitle={config?.mix_and_match_buttonRow_subtitle?.value}
          withDivider
          padding="8px"
          icon={
            <Icon name="plus" variant="ghost" colorMode="day" size="regular" />
          }
          onPrimaryActionClick={() => {
            hive.track('QBS_MODAL_OPEN')
            setOfferDetailsOpen(true);
          }}
        />
      )} */}
      {/** only show the buttonRow if there is only one active discount */}
      {/* {hasMultiTierQBSParam && savings?.length === 1 && (
        <ButtonRow
          ariaLabel="QBS Mix and Match"
          title={`Buy ${savings[0].quantity}+ ${savings[0].displayName}, Save ${savings[0].discount}%`}
          subtitle={config?.mix_and_match_buttonRow_subtitle?.value}
          withDivider
          padding="8px"
          icon={
            <Icon name="plus" variant="ghost" colorMode="day" size="regular" />
          }
          onPrimaryActionClick={() => {
            hive.track('QBS_MODAL_OPEN')
            setOfferDetailsOpen(true);
          }}
        />
      )} */}
      {!isGiftCard && (
        <>
          <ButtonRow
            ariaLabel="Shipping Policy"
            title={config?.shipping_policy_title?.value}
            titleWeight="light"
            subtitle={config?.shipping_policy_subheader?.value}
            failSafeLinkText={
              config?.shipping_policy_fail_safe_link_label?.value
            }
            withDivider
            padding="16px"
            icon={
              <Icon
                name="plus"
                variant="ghost"
                colorMode="day"
                size="regular"
              />
            }
            onPrimaryActionClick={() => {
              hive.track('FREE_SHIPPING_RETURNS_BUTTON_ROW_CLICK');
              setShippingAndReturnOpen(true);
            }}
          />
          <RichTextModal
            {...config.shipping_policy_modal}
            isOpen={isShippingAndReturnOpen}
            onRequestClose={() => setShippingAndReturnOpen(false)}
          />
        </>
      )}
      <ProductRatingV2 id={shopifyId} />
      {!callouts?.length && onShowFullDetails && (
        <ButtonRow
          title="Product & Material Details"
          titleWeight="light"
          onPrimaryActionClick={onShowFullDetails}
          icon={
            <Icon name="plus" variant="ghost" colorMode="day" size="regular" />
          }
          withDivider
          padding="16px"
        />
      )}
      {callouts?.length > 0 && (
        <Styled.ProductCalloutsContainer>
          <ButtonRow
            title={`Product Highlight${callouts.length > 1 ? 's' : ''}`}
            titleWeight="light"
            icon={
              <Icon
                name="plus"
                variant="ghost"
                colorMode="day"
                size="regular"
              />
            }
            withDivider={false}
            onPrimaryActionClick={onShowFullDetails}
          />
          <Styled.ProductCallouts
            callouts={callouts}
            onPrimaryActionClick={onShowFullDetails}
          />
          <LinkText size="Mini" underline onClick={onShowFullDetails}>
            See Product & Full Materials Details
          </LinkText>
        </Styled.ProductCalloutsContainer>
      )}
    </Styled.DetailLinks>
  );

  return (
    <section className={className}>
      <>
        <Styled.UpperWrapper>{upperWrapperElements}</Styled.UpperWrapper>
        <Styled.ImageWrapper>
          <ImageCarouselSmall images={images} />
        </Styled.ImageWrapper>
        <Styled.MiddleWrapper>{middleWrapperElements}</Styled.MiddleWrapper>
        <Styled.LowerWrapper $styleQueryMode={styleQueryMode}>
          {lowerWrapperElements}
        </Styled.LowerWrapper>
        {config?.mix_and_match && (
          <RichTextModal
            {...config.mix_and_match}
            bulletType="default bullet"
            columnType="one column"
            isOpen={isOfferDetailsOpen}
            onRequestClose={() => setOfferDetailsOpen(false)}
          />
        )}
      </>
      {currentVariant.id && (
        <NotifyDialog
          isOpen={isNotifyOpen}
          onRequestClose={() => setIsNotifyOpen(false)}
          title={title}
          color={currentVariant.color?.title || ''}
          sizes={currentVariant.sizes}
          image={currentVariant.images?.[0]}
          sku={currentVariant.sku}
          productId={parseInt(productId, 10)}
          variantId={parseInt(currentVariant.externalSystems?.shopify, 10)}
        />
      )}
    </section>
  );
};

ProductDetails.propTypes = {
  /**
   * Product categories
   */
  categories: PropTypes.arrayOf(PropTypes.string),
  /**
   * Swatches objects
   */
  colors: PropTypes.arrayOf(PropTypes.shape(colorPropType)),
  /**
   * CSS Class Name
   */
  className: PropTypes.string,
  /**
   * Product variants
   */
  variants: PropTypes.arrayOf(PropTypes.shape(variantPropType)),
  /**
   * Sorted array of size options
   */
  sizes: PropTypes.arrayOf(PropTypes.shape(sizesPropType)).isRequired,
  /**
   * Size guides to include with product
   */
  guides: ProductPropTypes.sizeGuideInputs,
  /**
   * The ID of the product
   */
  id: PropTypes.string.isRequired,
  /**
   * The Shopify ID of the product
   */
  shopifyId: PropTypes.string.isRequired,
  /**
   * Callback fired when a user clicks the Add to Bag button
   */
  onAddToBag: PropTypes.func,
  /**
   * Callback fired when a user clicks the Full Product Details button
   */
  onShowFullDetails: PropTypes.func,
  /**
   * The name of the product
   */
  title: PropTypes.string.isRequired,
  /**
   * pdp configs from contentful
   */
  config: PropTypes.shape({
    shipping_policy_modal: PropTypes.shape({
      cta: PropTypes.string,
      display: PropTypes.bool,
      title: PropTypes.string,
      sections: PropTypes.arrayOf(PropTypes.shape({})),
      defaultSection: PropTypes.shape({})
    }),
    shipping_message: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.shape(RichTextPropTypes)
    }),
    shipping_policy_title: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string
    }),
    shipping_policy_subheader: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string
    }),
    shipping_policy_fail_safe_link_label: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string
    }),
    /**
     * Copy displayed when a product is discounted, such as a pack
     */
    discount_copy: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string
    }),
    /**
     * Comma delimited values of What categories are considered donations
     * and should show the donation count
     */
    donation_categories: PropTypes.shape({
      value: PropTypes.string
    }),
    mix_and_match: PropTypes.shape({
      cta: PropTypes.string,
      display: PropTypes.bool,
      title: PropTypes.string,
      sections: PropTypes.arrayOf(PropTypes.shape({})),
      defaultSection: PropTypes.shape({})
    }),
    mix_and_match_buttonRow_subtitle: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string
    }),
    // Amount needed to get free shipping on order
    free_shipping_threshold: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string
    })
  }),
  images: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string
    })
  ),
  /**
   * List of related products to display
   */
  relatedProducts: PropTypes.arrayOf(PropTypes.shape({})),
  /**
   * Product URL
   */
  url: PropTypes.string.isRequired,
  /**
   * Attribute value drivers to emphasize certain aspects
   * of the product
   */
  callouts: PropTypes.arrayOf(PropTypes.shape(productAttributePropType))
};

ProductDetails.defaultProps = {
  callouts: [],
  categories: [],
  className: null,
  colors: [],
  config: {},
  guides: [],
  images: [],
  onAddToBag: undefined,
  onShowFullDetails: undefined,
  relatedProducts: null,
  variants: []
};

ProductDetails.displayName = 'ProductDetails';

ProductDetails.fragment = ProductDetailsFragment;

export default ProductDetails;
